import type { Simplify, TupleToUnion } from 'type-fest'
import { z } from 'zod'
import type { NonEmptyArray } from './utility-types.ts'

/**
 * Creates a map from enum values to their corresponding IDs
 * @param enumValues array of enum values
 * @param idConstructor function to create an ID from an index
 * @returns a tuple containing the map and a converter function
 * @example
 * const [Values] = EnumBuilder('a', 'b', 'c')
 * const idMap = createEnumMap(Values, MyIdConstructor)
 * // idMap = { a: MyIdConstructor(1), b: MyIdConstructor(2), c: MyIdConstructor(3) }
 */
export function createEnumMap<
  T extends string,
  U extends (id: number) => unknown,
>(
  enumValues: T[],
  idConstructor: U
): [Record<T, ReturnType<U>>, (value: number) => T] {
  const map = enumValues.reduce(
    (acc, value, index) =>
      Object.assign(acc, { [value]: idConstructor(index + 1) }),
    {} as Record<T, ReturnType<U>>
  )

  const converter = (value: number): T => {
    if (value < 1 || value > enumValues.length) {
      throw new Error(`Invalid enum value: ${value}`)
    }

    const result = enumValues[value - 1]
    if (!result) {
      throw new Error(`Invalid enum value: ${value}`)
    }

    return result
  }

  return [map, converter]
}

/**
 * @param values the list of values to create an enum from
 * @returns a tuple containing the enum values, the zod schema for validating the enum, and a map object for all the enum values
 */
export const EnumBuilder = <
  Items extends string,
  Values extends Readonly<NonEmptyArray<Items>>,
>(
  ...values: Values
) => {
  const schema = z.enum(values)

  const map = values.reduce(
    (map, value) => Object.assign(map, { [value]: value }),
    {} as Simplify<Readonly<{ [Value in TupleToUnion<Values>]: Value }>>
  )

  return Object.assign([values, schema, map] as const, {
    default: <Value extends TupleToUnion<Values>>(value: Value) =>
      [values, schema.default(value), map] as const,
    catch: <Value extends TupleToUnion<Values>>(value: Value) =>
      [values, schema.catch(value), map] as const,
  })
}
